import type { PlayerSkin } from '@cube-rush/shared';
import { useEffect, useRef } from 'preact/hooks';
import { prepareCanvas } from '../canvas';
import { prefersReducedMotion } from '../dom';
import { ShowcaseScene } from './showcaseScene';
import './SkinShowcase.css';

export interface SkinShowcaseProps {
  skin: PlayerSkin;
  label: string;
}

/** The live, hopping preview of the player's cube with its trail. Fills its container. */
export function SkinShowcase({ skin, label }: SkinShowcaseProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // The animation loop reads the latest skin from here instead of restarting on every change.
  const skinRef = useRef(skin);
  skinRef.current = skin;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const scene = new ShowcaseScene();
    const still = prefersReducedMotion();
    let frame = 0;
    let last = performance.now();
    const render = (now: number) => {
      const dt = still ? 0 : Math.min(0.05, (now - last) / 1000);
      last = now;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      const ctx = prepareCanvas(canvas, width, height);
      if (ctx && width > 0 && height > 0) {
        scene.step(dt, width, height);
        scene.draw(ctx, skinRef.current, width, height);
      }
      frame = requestAnimationFrame(render);
    };
    frame = requestAnimationFrame(render);
    return () => cancelAnimationFrame(frame);
  }, []);

  return <canvas ref={canvasRef} class="skin-showcase" role="img" aria-label={label} />;
}
